/**
 * PDF Generation Service
 *
 * Provides client-side API calls to server-side PDF generation endpoints.
 * Replaces the legacy client-side jsPDF implementation with template-based server generation.
 */

import { Platform } from 'react-native';
import * as RNFS from 'react-native-fs';
// @ts-ignore - react-native-share is mocked on web
import Share from 'react-native-share';

import { getAuthService } from './auth/authServiceFactory';
import { saveToAndroidDownloads } from './utils/androidDownloadUtils';
import { getApiConfig, isWebpackDevLocalWebOrigin } from '../config/api';

import type { DocumentFormat } from '../types/pdf';

/** MIME type for PDF documents. */
export const PDF_MIME = 'application/pdf';
/** MIME type for Word (.docx) documents. */
export const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Resolves the MIME type for a generated-document format. */
export const mimeForFormat = (format: DocumentFormat): string =>
  format === 'docx' ? DOCX_MIME : PDF_MIME;

const getApiUrl = (): string => {
  const url = getApiConfig().BASE_URL;
  if (!url && !(Platform.OS === 'web' && isWebpackDevLocalWebOrigin())) {
    console.error(
      `❌ API_BASE_URL not configured for platform: ${Platform.OS}\n` +
        `PDF generation will not work without a valid API URL.`,
    );
  }
  return url;
};

/**
 * Generates a blank request form for printing/handwriting
 * Calls server endpoint: GET /api/documents/blank
 *
 * @param format - 'pdf' (default) or 'docx' (Word)
 * @returns Promise<Blob | string> - Blob for web, base64 data URI for React Native
 */
export const generateBlankPDF = async (
  format: DocumentFormat = 'pdf',
): Promise<Blob | string> => {
  try {
    // Get auth token for native platforms (web uses cookies)
    const authService = getAuthService();
    const token = await authService.getToken();

    const headers: Record<string, string> = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Add API token if configured (for QA/Production environments)
    const apiToken = getApiConfig().API_TOKEN;
    if (apiToken) {
      headers['x-api-token'] = apiToken;
    }

    const query = format === 'docx' ? '?format=docx' : '';
    const response = await fetch(`${getApiUrl()}/api/documents/blank${query}`, {
      method: 'GET',
      headers,
      credentials: 'include', // Include cookies for web authentication (SA-00-02)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate blank document');
    }

    if (Platform.OS === 'web') {
      // Return blob for web
      return await response.blob();
    } else {
      // Convert to base64 for React Native
      const arrayBuffer = await response.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          '',
        ),
      );
      return `data:${mimeForFormat(format)};base64,${base64}`;
    }
  } catch (error: any) {
    console.error('Error generating blank document:', error);
    throw new Error(`Failed to generate blank document: ${error.message}`);
  }
};

/**
 * Options for embedding image attachments into the filled document (#1340).
 * Omit entirely for a text-only report (backward-compatible).
 */
export interface PdfImageOptions {
  /** Embed every image attachment on the request. */
  includeAll?: boolean;
  /** Image attachment ids to embed when includeAll is false. */
  selectedIds?: number[];
  /** How many images per page (1–4, default 2 = columns × rows). */
  imagesPerPage?: number;
  /** Grid columns for the PDF page layout (1 or 2). Disambiguates 1×2 vs 2×1. */
  columns?: number;
}

/**
 * Generates a filled document with form data
 * Calls server endpoint: POST /api/documents/filled
 *
 * @param formData - Object containing all form field data
 * @param imageOptions - Optional image-embedding directive (#1340). When omitted,
 *   the report is text-only exactly as before. Embedded images apply to both the
 *   PDF and Word (DOCX) output since they share the DOCX render path.
 * @param format - 'pdf' (default) or 'docx' (Word) (#1390)
 * @returns The file bytes and the server download name. Blob for web, base64 data URI for React Native.
 */
export interface GeneratedDocument {
  data: Blob | string;
  fileName: string;
}

const exportExtension = (format: DocumentFormat): 'pdf' | 'docx' =>
  format === 'docx' ? 'docx' : 'pdf';

const fallbackExportFileName = (
  formData: Record<string, unknown>,
  format: DocumentFormat,
): string => {
  const id =
    formData.id === undefined || formData.id === null || formData.id === ''
      ? 'Unknown'
      : String(formData.id);
  return `VertivForm_Request${id}.${exportExtension(format)}`;
};

const fileNameFromContentDisposition = (
  header: string | null,
): string | null => {
  if (!header) {
    return null;
  }
  const match = /filename="([^"]+)"/i.exec(header);
  const name = match?.[1]?.trim() ?? '';
  if (
    !name ||
    name.includes('/') ||
    name.includes('\\') ||
    name.includes('..')
  ) {
    return null;
  }
  return name;
};

export const generateFilledPDF = async (
  formData: Record<string, unknown>,
  imageOptions?: PdfImageOptions,
  format: DocumentFormat = 'pdf',
): Promise<GeneratedDocument> => {
  try {
    // Get auth token for native platforms (web uses cookies)
    const authService = getAuthService();
    const token = await authService.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // Add API token if configured (for QA/Production environments)
    const apiToken = getApiConfig().API_TOKEN;
    if (apiToken) {
      headers['x-api-token'] = apiToken;
    }

    // Only include imageOptions when the caller asked for images, keeping the
    // request body identical to the legacy text-only shape otherwise.
    const hasImageSelection =
      !!imageOptions &&
      (imageOptions.includeAll === true ||
        (imageOptions.selectedIds?.length ?? 0) > 0);

    const response = await fetch(`${getApiUrl()}/api/documents/filled`, {
      method: 'POST',
      headers,
      credentials: 'include', // Include cookies for web authentication
      body: JSON.stringify(
        hasImageSelection
          ? { formData, imageOptions, format }
          : { formData, format },
      ),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate filled document');
    }

    const disposition = response.headers?.get?.('Content-Disposition') ?? null;
    const fileName =
      fileNameFromContentDisposition(disposition) ??
      fallbackExportFileName(formData, format);

    if (Platform.OS === 'web') {
      return { data: await response.blob(), fileName };
    }

    const arrayBuffer = await response.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(arrayBuffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        '',
      ),
    );
    return {
      data: `data:${mimeForFormat(format)};base64,${base64}`,
      fileName,
    };
  } catch (error: any) {
    console.error('Error generating filled document:', error);
    throw new Error(`Failed to generate filled document: ${error.message}`);
  }
};

/**
 * Result of a PDF download operation
 */
export interface DownloadResult {
  success: boolean;
  /** Only populated on iOS where the local path is used for the share sheet. */
  filePath?: string;
  message: string;
}

/**
 * Downloads or shares a generated document (PDF or Word)
 *
 * Platform behavior:
 * - Web: Downloads directly to browser's default download location
 * - Android: Saves directly to Downloads folder
 * - iOS: Saves to app documents and opens share sheet (iOS convention)
 *
 * @param pdfData - Blob (web) or base64 data URI string (React Native)
 * @param filename - Desired filename (extension implies the format)
 * @param mimeType - Content MIME type (defaults to application/pdf)
 * @returns DownloadResult with success status and file path (mobile only)
 */
export const downloadPDF = async (
  pdfData: Blob | string,
  filename: string,
  mimeType: string = PDF_MIME,
): Promise<DownloadResult> => {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    if (pdfData instanceof Blob) {
      // Web download with Blob
      const url = URL.createObjectURL(pdfData);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Web download with data URI
      const link = document.createElement('a');
      link.href = pdfData;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    return { success: true, message: 'PDF downloaded' };
  } else if (Platform.OS === 'android') {
    try {
      if (typeof pdfData !== 'string') {
        throw new Error('Expected data URI string for React Native');
      }

      const base64Data = pdfData.split(',')[1];
      if (!base64Data) {
        throw new Error('Invalid document data URI');
      }

      await saveToAndroidDownloads(base64Data, filename, mimeType);

      return {
        success: true,
        message: 'Saved to Downloads folder',
      };
    } catch (error: any) {
      console.error('Error saving PDF to Downloads:', error);
      throw error;
    }
  } else {
    // iOS - save to documents and use share sheet (standard iOS pattern)
    // iOS doesn't have a user-accessible Downloads folder like Android
    try {
      if (typeof pdfData !== 'string') {
        throw new Error('Expected data URI string for React Native');
      }

      const base64Data = pdfData.split(',')[1];
      if (!base64Data) {
        throw new Error('Invalid document data URI');
      }

      // Save to app's document directory first
      const filePath = `${RNFS.DocumentDirectoryPath}/${filename}`;
      await RNFS.writeFile(filePath, base64Data, 'base64');
      console.log('Document saved to:', filePath);

      // Use share sheet on iOS - this is the standard iOS pattern
      // Users can choose "Save to Files" to save to their preferred location
      if (Share && Share.open) {
        await Share.open({
          title: 'Save Document',
          url: `file://${filePath}`,
          type: mimeType,
          subject: filename,
          saveToFiles: true, // Prioritize save to files option
        });
        console.log('Document shared successfully');
      }

      return {
        success: true,
        filePath,
        message: 'Document ready to save',
      };
    } catch (error: any) {
      // User cancelled share sheet - this is not an error
      if (
        error?.message?.includes('User did not share') ||
        error?.message?.includes('CANCELLED')
      ) {
        console.log('PDF share cancelled by user');
        return {
          success: true,
          message: 'PDF save cancelled',
        };
      }

      console.error('Error saving/sharing PDF:', error);
      throw error;
    }
  }
};
